import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, X, Send, Ban, Download, Link2, DollarSign, Eye, ShieldAlert, Clock } from "lucide-react";
import { api, API } from "@/lib/api";
import { INVOICE_STATUSES, PAYMENT_METHODS } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, inputCls, labelCls, AInput, ASelect, ATextarea, useSave } from "../AdminUI";

const sColor = (s) => ({ Paid: "green", Voided: "red", Cancelled: "red", Overdue: "red", Disputed: "red",
  "Partially Paid": "amber", "Payment Pending": "amber", "Billing Information Submitted": "amber",
  Draft: "gray", Sent: "blue", Delivered: "blue", Viewed: "navy" }[s] || "gray");

const downloadPdf = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
};

function Metrics() {
  const [d, setD] = useState(null);
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showReport, setShowReport] = useState(false);

  const loadDash = () => {
    const p = new URLSearchParams();
    if (preset === "mtd" || preset === "ytd") p.set("preset", preset);
    else if (preset === "custom") { if (from) p.set("date_from", from); if (to) p.set("date_to", to); }
    api.get(`/admin/billing/dashboard${p.toString() ? `?${p}` : ""}`).then((r) => setD(r.data)).catch(() => {});
  };
  useEffect(() => { loadDash(); }, [preset, from, to]); // eslint-disable-line
  useEffect(() => { if (showReport) api.get(`/admin/billing/report?year=${year}`).then((r) => setReport(r.data)).catch(() => {}); }, [showReport, year]);
  if (!d) return null;
  const cells = [["Draft", d.draft], ["Sent", d.sent], ["Viewed", d.viewed], ["Overdue", d.overdue],
    ["Paid", d.paid], ["Pending Pmt", d.payment_pending]];
  const presets = [["all", "All Time"], ["mtd", "MTD"], ["ytd", "YTD"], ["custom", "Custom"]];
  const maxAnnual = report ? Math.max(1, ...report.annual.map((a) => a.invoiced)) : 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2" data-testid="report-filters">
        <div className="flex rounded-md border border-gray-200 overflow-hidden">
          {presets.map(([v, l]) => (
            <button key={v} onClick={() => setPreset(v)} className={`px-3 py-1.5 text-xs font-semibold ${preset === v ? "bg-[var(--navy)] text-white" : "bg-white text-gray-500"}`} data-testid={`preset-${v}`}>{l}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 px-2 rounded border border-gray-300 text-xs" data-testid="report-from" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 px-2 rounded border border-gray-300 text-xs" data-testid="report-to" />
          </div>
        )}
        <button onClick={() => setShowReport((s) => !s)} className="ml-auto text-xs font-semibold text-[var(--action)]" data-testid="toggle-report">{showReport ? "Hide" : "Show"} Multi-Year Report</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="billing-metrics">
        <div className="bg-[var(--navy)] text-white rounded-xl p-4 col-span-2 md:col-span-3 lg:col-span-2">
          <p className="text-xs uppercase text-white/60">Collected / Invoiced{d.preset ? ` (${d.preset.toUpperCase()})` : d.date_from ? " (range)" : ""}</p>
          <p className="text-2xl font-head font-extrabold">${d.total_collected?.toLocaleString()} <span className="text-white/50 text-base">/ ${d.total_invoiced?.toLocaleString()}</span></p>
          <p className="text-xs text-[var(--bright)] mt-1">Outstanding: ${d.outstanding?.toLocaleString()}</p>
        </div>
        {cells.map(([l, v]) => (
          <div key={l} className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-2xl font-head font-extrabold text-[var(--navy)]">{v}</p><p className="text-xs text-gray-500">{l}</p></div>
        ))}
      </div>

      {showReport && report && (
        <Card title={<span className="flex items-center justify-between w-full">Revenue Report
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-8 px-2 rounded border border-gray-300 text-sm text-[var(--navy)]" data-testid="report-year">
            {report.available_years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></span>}>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase text-gray-400 font-semibold mb-2">Monthly — {report.year} (Invoiced ${report.year_invoiced?.toLocaleString()} · Collected ${report.year_collected?.toLocaleString()})</p>
              <table className="w-full text-sm" data-testid="report-monthly">
                <thead><tr className="text-left text-gray-400 text-xs border-b"><th className="py-1">Month</th><th className="text-right">Invoiced</th><th className="text-right">Collected</th></tr></thead>
                <tbody>{report.monthly.map((m) => (
                  <tr key={m.month} className="border-b border-gray-50"><td className="py-1.5">{m.label}</td><td className="text-right">${m.invoiced.toLocaleString()}</td><td className="text-right text-green-600">${m.collected.toLocaleString()}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-400 font-semibold mb-2">Year-over-Year</p>
              <div className="space-y-2" data-testid="report-annual">
                {report.annual.map((a) => (
                  <div key={a.year}>
                    <div className="flex justify-between text-sm"><span className="font-semibold text-[var(--navy)]">{a.year}</span><span>${a.invoiced.toLocaleString()} <span className="text-green-600">(${a.collected.toLocaleString()} collected)</span></span></div>
                    <div className="h-2 bg-gray-100 rounded-full mt-1"><div className="h-2 bg-[var(--action)] rounded-full" style={{ width: `${Math.round((a.invoiced / maxAnnual) * 100)}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function RevealModal({ invoice, onClose }) {
  const [f, setF] = useState({ password: "", mfa_code: "", reason: "", confirm_terminal: false });
  const [saving, run] = useSave();
  const submit = (e) => { e.preventDefault(); run(async () => {
    await api.post(`/admin/invoices/${invoice.id}/reveal`, f);
  }, "Reveal approved"); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="reveal-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-3">
        <div className="flex items-center gap-2 text-[var(--red)]"><ShieldAlert className="w-5 h-5" /><h2 className="font-head font-bold text-lg uppercase">Reveal for Offline Processing</h2></div>
        <p className="text-xs text-gray-500">Requires re-authentication. Card collection is disabled until a secure PCI-compliant vault is connected — this will report that no vault is available.</p>
        <AInput label="Confirm Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="reveal-password" />
        <AInput label="MFA Code (if enabled)" value={f.mfa_code} onChange={(e) => setF({ ...f, mfa_code: e.target.value })} />
        <AInput label="Processing Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required data-testid="reveal-reason" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.confirm_terminal} onChange={(e) => setF({ ...f, confirm_terminal: e.target.checked })} data-testid="reveal-confirm" /> I am actively entering this into the approved offline terminal now.</label>
        <div className="flex gap-2 justify-end"><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn variant="danger" type="submit" loading={saving} data-testid="reveal-submit">Reveal</Btn></div>
      </form>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [f, setF] = useState({ amount: invoice.balance_due, payment_date: "", payment_method: "Cash", processor_txn_id: "", notes: "", status: "Approved" });
  const [saving, run] = useSave();
  const submit = (e) => { e.preventDefault(); run(async () => {
    await api.post(`/admin/invoices/${invoice.id}/payments`, { ...f, amount: parseFloat(f.amount) });
    onSaved(); onClose();
  }, "Payment recorded"); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="payment-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-3">
        <h2 className="font-head font-bold text-lg uppercase text-[var(--navy)]">Record Payment</h2>
        <p className="text-xs text-gray-500">Balance due: ${invoice.balance_due?.toFixed(2)}</p>
        <AInput label="Amount ($)" type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="pay-amount" />
        <AInput label="Payment Date" type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} />
        <ASelect label="Method" value={f.payment_method} options={PAYMENT_METHODS} onChange={(e) => setF({ ...f, payment_method: e.target.value })} data-testid="pay-method" />
        <AInput label="Processor / Reference #" value={f.processor_txn_id} onChange={(e) => setF({ ...f, processor_txn_id: e.target.value })} />
        <ATextarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
        <div className="flex gap-2 justify-end"><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" loading={saving} data-testid="pay-save">Save Payment</Btn></div>
      </form>
    </div>
  );
}

function InvoiceDetail({ id, canVoid, onClose, onChange }) {
  const [inv, setInv] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [saving, run] = useSave();
  const load = () => api.get(`/admin/invoices/${id}`).then((r) => setInv(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!inv) return null;
  const send = () => run(async () => { const r = await api.post(`/admin/invoices/${id}/send`); if (!r.data.delivered) toast.error("Send failed — invoice preserved. Use Copy Secure Link."); await load(); onChange?.(); }, "Invoice sent");
  const voidInv = () => { const reason = window.prompt("Reason to void:"); if (reason === null) return; run(async () => { await api.post(`/admin/invoices/${id}/void`, { reason }); await load(); onChange?.(); }, "Voided"); };
  const copyLink = async () => { const r = await api.post(`/admin/invoices/${id}/copy-link`); await navigator.clipboard?.writeText(r.data.secure_link).catch(() => {}); toast.success("Secure link copied"); };

  const F = ({ l, v }) => v ? <div><p className="text-[11px] uppercase text-gray-400 font-semibold">{l}</p><p className="text-sm text-[var(--navy)]">{v}</p></div> : null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="invoice-detail">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--surface)] h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <div><h2 className="font-head font-bold text-xl text-[var(--navy)]">{inv.invoice_number}</h2>
            <p className="text-xs text-gray-400">{inv.customer_name} · <Badge color={sColor(inv.status)}>{inv.status}</Badge></p></div>
          <button onClick={onClose} data-testid="invoice-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Btn variant="blue" loading={saving} onClick={send} data-testid="send-invoice"><Send className="w-4 h-4" /> Send</Btn>
            <Btn variant="ghost" onClick={copyLink} data-testid="copy-link"><Link2 className="w-4 h-4" /> Copy Link</Btn>
            <Btn variant="ghost" onClick={() => downloadPdf(`/admin/invoices/${id}/pdf`, `${inv.invoice_number}.pdf`)} data-testid="invoice-pdf"><Download className="w-4 h-4" /> PDF</Btn>
            <Btn variant="navy" onClick={() => setShowPay(true)} data-testid="record-payment"><DollarSign className="w-4 h-4" /> Record Payment</Btn>
            <Btn variant="danger" onClick={() => setShowReveal(true)} data-testid="reveal-btn"><ShieldAlert className="w-4 h-4" /> Reveal Card</Btn>
            {canVoid && inv.status !== "Voided" && <Btn variant="danger" onClick={voidInv} data-testid="void-invoice"><Ban className="w-4 h-4" /> Void</Btn>}
          </div>

          <Card title="Summary">
            <div className="grid grid-cols-2 gap-3">
              <F l="Invoice Date" v={inv.invoice_date} /><F l="Due Date" v={inv.due_date} />
              <F l="Total" v={`$${inv.total?.toFixed(2)}`} /><F l="Amount Paid" v={`$${(inv.amount_paid||0).toFixed(2)}`} />
              <F l="Balance Due" v={`$${inv.balance_due?.toFixed(2)}`} /><F l="Type" v={inv.invoice_type} />
              <F l="Email" v={inv.customer_email} /><F l="Phone" v={inv.customer_phone} />
              <F l="Service Address" v={inv.service_address} />
              {inv.quote_id && <F l="Quote" v={inv.quote_id} />}{inv.job_id && <F l="Job" v={inv.job_id} />}
              {inv.financing_ref && <F l="Financing" v="EasyQuickFund referral linked" />}
            </div>
          </Card>

          <Card title="Line Items">
            <table className="w-full text-sm">
              <tbody>{(inv.line_items||[]).map((it,i) => (
                <tr key={i} className="border-b border-gray-50"><td className="py-1.5">{it.description}</td><td className="text-right text-gray-500">{it.qty} × ${Number(it.rate).toFixed(2)}</td><td className="text-right font-semibold w-24">${(it.qty*it.rate||0).toFixed(2)}</td></tr>
              ))}</tbody>
            </table>
          </Card>

          {inv.billing_info?.consent_at && (
            <Card title="Client Billing Information">
              <div className="grid grid-cols-2 gap-3">
                <F l="Name" v={`${inv.billing_info.first_name||""} ${inv.billing_info.last_name||""}`} />
                <F l="Company" v={inv.billing_info.company_name} />
                <F l="Billing Address" v={inv.billing_info.billing_address} />
                <F l="City/State/ZIP" v={`${inv.billing_info.city||""} ${inv.billing_info.state||""} ${inv.billing_info.zip||""}`} />
                <F l="Method Preference" v={inv.billing_info.payment_method_preference} />
                <F l="Consent" v={new Date(inv.billing_info.consent_at).toLocaleString()} />
              </div>
              <p className="text-xs text-gray-400 mt-2">Payment details are masked by default. Raw card data is never stored on this website.</p>
            </Card>
          )}

          <Card title={<span className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Payments</span>}>
            {inv.payments?.length ? inv.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 text-sm">
                <span>${p.amount?.toFixed(2)} · {p.payment_method} · <Badge color={p.status==="Approved"||p.status==="Paid"?"green":p.status==="Declined"||p.status==="Failed"?"red":"amber"}>{p.status}</Badge></span>
                {p.receipt_number && <button onClick={() => downloadPdf(`/admin/payments/${p.id}/receipt`, `${p.receipt_number}.pdf`)} className="text-[var(--action)] text-xs" data-testid={`receipt-${p.receipt_number}`}>Receipt</button>}
              </div>
            )) : <p className="text-sm text-gray-400">No payments recorded.</p>}
          </Card>

          <Card title={<span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Timeline</span>}>
            <div className="space-y-2">{(inv.timeline||[]).slice().reverse().map((t,i) => (
              <div key={i} className="flex gap-3 text-sm"><span className="text-gray-400 text-xs w-32 shrink-0">{new Date(t.at).toLocaleString()}</span><span>{t.text}</span></div>
            ))}</div>
          </Card>
        </div>
      </div>
      {showPay && <PaymentModal invoice={inv} onClose={() => setShowPay(false)} onSaved={() => { load(); onChange?.(); }} />}
      {showReveal && <RevealModal invoice={inv} onClose={() => setShowReveal(false)} />}
    </div>
  );
}

function NewInvoiceModal({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: "", customer_name: "", customer_email: "", service_address: "", due_date: "",
    service_description: "", line_items: [{ description: "", qty: 1, rate: 0 }], tax: 0, discount: 0, payment_instructions: "" });
  const [saving, run] = useSave();
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const pickCust = (id) => { const c = customers.find((x) => x.id === id); setF({ ...f, customer_id: id, customer_name: c?.name || "", customer_email: c?.email || "", customer_phone: c?.phone || "", service_address: c?.address || "", company_name: c?.company_name || "" }); };
  const setItem = (i, k, v) => setF({ ...f, line_items: f.line_items.map((it, ix) => ix === i ? { ...it, [k]: v } : it) });
  const submit = (e) => { e.preventDefault(); run(async () => {
    const r = await api.post("/admin/invoices", { ...f, tax: parseFloat(f.tax || 0), discount: parseFloat(f.discount || 0),
      line_items: f.line_items.map((i) => ({ ...i, qty: parseFloat(i.qty || 0), rate: parseFloat(i.rate || 0) })) });
    onCreated(r.data.id); onClose();
  }, "Invoice created"); };
  const total = f.line_items.reduce((s, i) => s + (i.qty * i.rate || 0), 0) - (f.discount || 0) + (parseFloat(f.tax) || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="new-invoice-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <h2 className="font-head font-bold text-xl uppercase text-[var(--navy)]">New Invoice</h2>
        <div className="grid grid-cols-2 gap-3">
          <ASelect label="Customer" value={f.customer_id} options={[{ value: "", label: "— Select —" }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.customer_id})` }))]} onChange={(e) => pickCust(e.target.value)} data-testid="inv-customer" />
          <AInput label="Due Date" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
          <AInput label="Customer Name" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} required data-testid="inv-name" />
          <AInput label="Customer Email" value={f.customer_email} onChange={(e) => setF({ ...f, customer_email: e.target.value })} data-testid="inv-email" />
          <AInput label="Service Address" value={f.service_address} onChange={(e) => setF({ ...f, service_address: e.target.value })} />
        </div>
        <AInput label="Service Description" value={f.service_description} onChange={(e) => setF({ ...f, service_description: e.target.value })} />
        <div><label className={labelCls}>Line Items</label>
          {f.line_items.map((it, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={`${inputCls} flex-1`} placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`inv-item-${i}`} />
              <input className={`${inputCls} w-16`} type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
              <input className={`${inputCls} w-24`} type="number" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} />
            </div>
          ))}
          <Btn variant="ghost" type="button" onClick={() => setF({ ...f, line_items: [...f.line_items, { description: "", qty: 1, rate: 0 }] })}><Plus className="w-4 h-4" /> Add Item</Btn>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AInput label="Discount ($)" type="number" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} />
          <AInput label="Tax ($)" type="number" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} />
        </div>
        <div className="text-right font-bold text-[var(--navy)]">Total: <span data-testid="inv-total">${total.toFixed(2)}</span></div>
        <div className="flex gap-2 justify-end"><Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn><Btn type="submit" loading={saving} data-testid="inv-save">Create Invoice</Btn></div>
      </form>
    </div>
  );
}

export default function Invoices({ canVoid }) {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get(`/admin/invoices?${q ? `q=${encodeURIComponent(q)}&` : ""}${status ? `status=${encodeURIComponent(status)}` : ""}`).then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Invoices</h1>
        <Btn onClick={() => setShowNew(true)} data-testid="new-invoice-btn"><Plus className="w-4 h-4" /> New Invoice</Btn>
      </div>
      <Metrics />
      <div className="flex flex-wrap gap-3">
        <input className={`${inputCls} flex-1 min-w-[200px]`} placeholder="Search invoice #, customer, email, quote, job…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search" />
        <select className={`${inputCls} w-auto`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter">
          <option value="">All statuses</option>{INVOICE_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <Card>
        {invoices.length === 0 ? <EmptyState icon={FileText} title="No invoices yet" hint="Create an invoice or convert an approved quote." /> : (
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b"><th className="py-2 px-2">Invoice</th><th className="px-2">Customer</th><th className="px-2">Total</th><th className="px-2">Balance</th><th className="px-2">Status</th></tr></thead>
              <tbody>{invoices.map((i) => (
                <tr key={i.id} onClick={() => setOpenId(i.id)} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" data-testid={`invoice-row-${i.invoice_number}`}>
                  <td className="py-3 px-2 font-semibold text-[var(--navy)]">{i.invoice_number}</td>
                  <td className="px-2 text-gray-600">{i.customer_name}</td>
                  <td className="px-2">${i.total?.toFixed(2)}</td>
                  <td className="px-2">${i.balance_due?.toFixed(2)}</td>
                  <td className="px-2"><Badge color={sColor(i.status)}>{i.status}</Badge></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>
      {openId && <InvoiceDetail id={openId} canVoid={canVoid} onClose={() => setOpenId(null)} onChange={load} />}
      {showNew && <NewInvoiceModal onClose={() => setShowNew(false)} onCreated={(id) => { load(); setOpenId(id); }} />}
    </div>
  );
}
